"use client"

import { allPartColors, colorPartsOf } from "@v2/lib/colorParts";
import axiosInstance from "@/api/axios";
import { BrandType } from "@/app/types/brand";
import { ProductDetailType } from "@/app/types/product-site";
import { useRouter } from "@bprogress/next/app";
import { Box, Button, colors, Divider, GlobalStyles, Grid2 as Grid, IconButton, Link as MuiLink, NoSsr, Paper, Slide, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import FlexBox from "@v2/components/styled/FlexBox";
import { numberFormat } from "@v2/helpers/optimizedHelpers";
import api from "@v2/lib/api";
import { uiColors } from "@v2/lib/color";
import { useAppSelector } from "@v2/lib/redux/hooks";
import copy from 'copy-text-to-clipboard';
import { CheckCircle, Heart, HeartPlus, Share2 } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { itemFromProductDetail, trackAddToWishlist, trackRemoveFromWishlist } from "@v2/lib/analytics";
import PdpAddToCartHandler from "../partials/PdpAddToCartHandler";
import PdpAddToTryCartHandler from "../partials/PdpAddToTryCartHandler";
import PdpBrandImage from "../partials/PdpBrandImage";

export default function PdpOperations({
    showProduct,
    brand,
    isTriable,
    inStockProducts,
    extra,
}: {
    extra: {
        siteProductId: number,
        siteProductSlug: string,
        siteProductTitle: string,
        isFavorite: boolean,
    }
    showProduct: ProductDetailType,
    brand: BrandType,
    isTriable: boolean,
    inStockProducts: ProductDetailType[],
}) {

    const pathname = usePathname()
    const router = useRouter()
    const { enqueueSnackbar } = useSnackbar()

    const inStock = showProduct.is_available !== false && (showProduct.need_inquiry || (showProduct.quantity > 0 && showProduct.final_price > 0))
    const discountPercent = (showProduct.discount / showProduct.price) * 100

    const theme = useTheme()
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
    const menuIsOpen = useAppSelector(s => s.general.headerIsShow)
    const isAuthenticated = useAppSelector(s => s.general.session?.isAuthenticated) ?? false

    const inStockSizes = inStockProducts.filter(i => i.color_code === showProduct.color_code);

    const clearCache = useCallback(() => {
        return fetch('/api/cache/clear/' + extra.siteProductSlug)
    }, [extra.siteProductSlug])

    const showAnotherColor = useCallback((pid: number) => () => {
        router.push(`${pathname}?pid=${pid}`)
    }, [pathname])


    const [isFavorite, setIsFavorite] = useState<boolean>(extra.isFavorite)

    useEffect(() => {
        if (!isAuthenticated) return;
        axiosInstance.get(`${process.env.NEXT_PUBLIC_BASE_API_URL}/api/v1/product/site/${extra.siteProductSlug}`)
            .then((res: { data?: { data?: { is_favorite?: boolean } } }) => {
                if (res.data?.data?.is_favorite !== undefined) {
                    setIsFavorite(Boolean(res.data.data.is_favorite));
                }
            })
            .catch(() => {});
    }, [isAuthenticated, extra.siteProductSlug]);

    const toggleFavorite = useCallback(async () => {
        if (!isAuthenticated) return router.push(`/fa/sign-in?callback_url=${pathname}`);

        const favoriteItem = itemFromProductDetail(showProduct, {
            brand: brand?.en_name,
            siteProductId: extra.siteProductId,
        })

        if (isFavorite) {
            setIsFavorite(false)
            enqueueSnackbar('کالا از لیست حذف شد')
            await axiosInstance.delete(api.favorite, {
                data: {
                    site_product_id: extra.siteProductId
                }
            })
            trackRemoveFromWishlist(favoriteItem)
        } else {
            setIsFavorite(true)
            enqueueSnackbar('کالا به لیست اضافه شد')

            await axiosInstance.post(api.favorite, {
                site_product_id: extra.siteProductId
            })
            trackAddToWishlist(favoriteItem)
        }

        await clearCache()


    }, [isFavorite, pathname, isAuthenticated, extra.siteProductSlug])


    const share = useCallback(() => {
        copy(`${process.env.NEXT_PUBLIC_HOME_URL}${pathname}?pid=${showProduct.product_id}`)
        enqueueSnackbar('لینک کالا کپی شد')
    }, [showProduct])
    return (
        <>
            <Box sx={{
                position: 'sticky',
                transition: '0.5s cubic-bezier(0.22,0.61,0.36,1)',
                top: menuIsOpen ? {
                    xs: 108 + 0
                } : 36,
                pt: 1,
                bgcolor: '#fff',
            }}>
                {(isDesktop || !inStock) && (
                    <Grid container alignItems={'center'}>
                        <Grid size={6}>
                            <PdpBrandImage brand={brand} />
                        </Grid>
                        <Grid size={6} sx={{
                            textAlign: 'right',
                        }}>
                            {inStock && (
                                <Box>
                                    {!!discountPercent && (
                                        <Box>
                                            <Typography component={'span'} sx={{
                                                bgcolor: uiColors.primary,
                                                borderRadius: 2,
                                                color: '#fff',
                                                fontSize: 13,
                                                py: '4px',
                                                px: '8px'
                                            }}>
                                                %{discountPercent.toFixed(0)}
                                            </Typography>
                                            <Typography component={'span'} sx={{
                                                fontSize: 14,
                                                mr: 1,
                                                textDecoration: 'line-through',
                                                color: colors.grey[600]
                                            }}>
                                                {String(numberFormat(showProduct.price)).replaceAll(',', '.')}
                                            </Typography>
                                        </Box>
                                    )}
                                    {showProduct.need_inquiry ? (
                                        <Typography sx={{
                                            fontSize: 14,
                                            fontWeight: 400,
                                            color: colors.grey[900],
                                            letterSpacing: 1,
                                        }}>
                                            نیازمند استعلام
                                        </Typography>
                                    ) : (
                                        <Box>
                                            <Typography component="span" sx={{
                                                fontSize: 18,
                                                fontWeight: 400,
                                                color: colors.grey[900],
                                                letterSpacing: 1,
                                            }}>{String(numberFormat(showProduct.final_price)).replaceAll(',', '.')}</Typography>
                                            <Typography component="span" sx={{
                                                fontSize: 14,
                                                fontWeight: 400,
                                                color: colors.grey[800],
                                                ml: 1
                                            }}>
                                                تومانءءء
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {!inStock && (
                                <Box sx={{
                                    py: 0.5,
                                    px: 2,
                                    bgcolor: colors.grey[600],
                                    display: 'inline-block',
                                    borderRadius: 4
                                }}>
                                    <Typography component="span" sx={{
                                        fontSize: 14,
                                        fontWeight: 400,
                                        color: '#fff',
                                    }}>
                                        ناموجود
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                )}

                {(inStock && isDesktop) && (
                    <Box sx={{ mt: 1 }}>
                        <PdpAddToCartHandler showProduct={showProduct} brandName={brand?.en_name} siteProductId={extra.siteProductId} site_product_details={{
                            slug: extra.siteProductSlug,
                            title: extra.siteProductTitle
                        }} />
                    </Box>
                )}

                {(inStock && isTriable && isDesktop && !showProduct.need_inquiry) && (<>
                    <Typography sx={{
                        mt: 3,
                        display: 'inline-flex',
                        alignItems: 'center',
                        '& > b': {
                            color: colors.green[800],
                            display: 'inline-block',
                            px: '4px'
                        },
                        '& > svg': {
                            mr: 1,
                        },
                        fontSize: 14,
                        mb: 1,
                    }}>
                        <CheckCircle color={colors.green[800]} />
                        <span>
                            امکان ارسال و
                            <Typography component="b" sx={{ mx: 0.5, fontWeight: 600 }}>پرو&nbsp;</Typography>
                            در محل قبل از خرید
                        </span>
                    </Typography>
                    <PdpAddToTryCartHandler showProduct={showProduct} site_product_details={{
                        slug: extra.siteProductSlug,
                        title: extra.siteProductTitle
                    }} />
                </>)}

                {(!inStock && inStockSizes.length > 0) && (
                    <Box sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: 40,
                    }}>
                        <Typography component="span" sx={{ fontSize: 14, color: colors.grey[800] }}>همین رنگ موجود در {inStockProducts.filter(i => i.color_code === showProduct.color_code).length} سایز دیگر </Typography>
                        <Box className="_colors" sx={{
                            display: 'flex',
                            direction: 'rtl',
                            width: (inStockSizes.length * 18) - ((inStockSizes.length - 1) * 7),
                        }}>

                            {inStockSizes.map((product, pi) => {
                                return (
                                    <Tooltip key={pi} title={product.size} >
                                        <Box key={pi} className="_color_item" onClick={showAnotherColor(product.product_id)} sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: 22,
                                            width: 22,
                                            minWidth: 22,
                                            borderRadius: '50%',
                                            border: '1px solid',
                                            borderColor: colors.grey[700],
                                            cursor: 'pointer',
                                            // display: 'inline-block',
                                            position: 'relative',
                                            right: (pi * 7) * -1,
                                            bgcolor: '#fff',
                                        }}>
                                            <Typography sx={{ fontSize: 11, color: colors.grey[700], }}>
                                                {product.size}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                )
                            })}
                        </Box>
                    </Box>
                )}

                {(!inStock && inStockProducts.length > 0 && inStockProducts.filter(i => i.color_code !== showProduct.color_code).length > 0) && (
                    <Box sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: 40,
                        '&:hover': {
                            '& ._colors': {
                                '& > ._color_item': {
                                    right: '0',
                                    borderColor: 'transparent',
                                    ml: 0.5,
                                }
                            }
                        }
                    }}>

                        <Typography component="span" sx={{ fontSize: 14, color: colors.grey[800] }}> موجود در {inStockProducts.length} رنگ دیگر </Typography>
                        <Box className="_colors" sx={{
                            display: 'flex',
                            direction: 'rtl',
                            width: (inStockProducts.length * 18) - ((inStockProducts.length - 1) * 7),
                        }}>

                            {inStockProducts.map((product, pi) => {
                                const parts = colorPartsOf(product)
                                const color = allPartColors(parts)[0]
                                // The first part plain, the others named: «مشکی لنز دودی».
                                const colorsTitle = parts.map((part, index) => (index ? part.title + ' ' : '') + part.colors.map(c => c.title).join(', ')).join(' ')
                                return (
                                    <Tooltip key={pi} title={colorsTitle} >
                                        <Box className="_color_item" onClick={showAnotherColor(product.product_id)} sx={{
                                            display: 'block',
                                            transition: '200ms',
                                            height: 22,
                                            width: 22,
                                            minWidth: 22,
                                            borderRadius: '50%',
                                            border: '2px solid #fff',
                                            cursor: 'pointer',
                                            // display: 'inline-block',
                                            position: 'relative',
                                            right: (pi * 7) * -1,
                                            bgcolor: color?.code ?? '#808080',
                                            backgroundImage: color?.texture ? `url('${process.env.NEXT_PUBLIC_MEDIA_URL}/api/v1/media/${color.texture.id}?height=38')` : undefined,
                                            backgroundSize: 'cover'
                                        }} />
                                    </Tooltip>
                                )
                            })}
                        </Box>
                    </Box>
                )}

                {(isDesktop || !inStock) && (
                    <>
                        <Button color="inherit" sx={{
                            bgcolor: colors.grey[50],
                            fontWeight: 400,
                            pl: 2,
                            justifyContent: 'flex-start',
                            width: 1,
                            height: 48,
                            mt: 3
                        }}
                            aria-label="add to try cart"
                            onClick={toggleFavorite}
                            startIcon={
                                isFavorite
                                    ? <Heart fill={colors.red[900]} color={colors.red[900]} />
                                    : <HeartPlus color={colors.red[900]} />
                            }
                        >
                            {isFavorite ? "حذف از لیست علاقه‌مندی‌ها" : "افزودن به لیست علاقه‌مندی‌ها"}
                        </Button>

                        <Button color="inherit" sx={{
                            bgcolor: colors.grey[50],
                            fontWeight: 400,
                            pl: 2,
                            justifyContent: 'flex-start',
                            width: 1,
                            height: 48,
                            mt: 1

                        }} onClick={share} startIcon={<Share2 />}>اشتراک گذاری</Button>
                        <Divider sx={{ mt: 3 }} />
                    </>
                )}

                <FlexBox sx={{
                    justifyContent: 'space-between'
                }}>
                    <Typography component="span" className="en-num" sx={{
                        display: 'inline-block',
                        color: colors.grey[600],
                        fontSize: 14,
                        mt: 1,
                        '& >span': {
                            mr: 1
                        }
                    }}>
                        <span> کد کالا:</span>
                        {showProduct.name}
                    </Typography>
                </FlexBox>
            </Box>

            <NoSsr>
            {!isDesktop && inStock && ( // mobile bottom fixed nav
                <>
                {/* The page can not tell on the server whether this bar shows — it follows
                    the variant picked here — so the bar reserves its own space. */}
                <GlobalStyles styles={{ ':root': { '--pdp-bottom-bar-space': '120px' } }} />
                <Slide in={true} direction="up">
                    <Paper elevation={8} sx={{
                        borderRadius: '12px 12px 0 0',
                        position: 'fixed',
                        zIndex: 10,
                        bottom: 0, right: 0, left: 0,
                        p: 1,
                        bgcolor: '#fff',

                    }}>
                        <Grid container alignItems={'center'} sx={{
                            height: 56,
                        }}>
                            <Grid size={5} sx={{

                                '& > button': {
                                    height: 40, width: 40,
                                    mr: 1
                                }
                            }} >
                                <IconButton aria-label="toggle favorite" onClick={toggleFavorite}>
                                    {isFavorite
                                        ? <Heart fill={colors.red[900]} color={colors.red[900]} />
                                        : <HeartPlus color={colors.red[900]} />}
                                </IconButton>
                                <IconButton aria-label="share" onClick={share}>
                                    <Share2 />
                                </IconButton>
                            </Grid>
                            <Grid size={7} sx={{
                                textAlign: 'right',
                            }}>
                                {inStock && !showProduct.need_inquiry && (
                                    <Box>
                                        {!!discountPercent && (
                                            <Box>
                                                <Typography component={'span'} sx={{
                                                    bgcolor: uiColors.primary,
                                                    borderRadius: 2,
                                                    color: '#fff',
                                                    fontSize: 13,
                                                    py: 0.5,
                                                    px: 1
                                                }}>
                                                    %{discountPercent.toFixed(0)}
                                                </Typography>
                                                <Typography component={'span'} sx={{
                                                    fontSize: 14,
                                                    mr: 1,
                                                    textDecoration: 'line-through',
                                                    color: colors.grey[600]
                                                }}>
                                                    {String(numberFormat(showProduct.price)).replaceAll(',', '.')}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box>
                                            <Typography component="span" sx={{
                                                fontSize: 18,
                                                fontWeight: 600,
                                                color: colors.grey[900],
                                                letterSpacing: 1,
                                            }}>{String(numberFormat(showProduct.final_price)).replaceAll(',', '.')}</Typography>
                                            <Typography component="span" sx={{
                                                fontSize: 14,
                                                fontWeight: 400,
                                                color: colors.grey[800],
                                                ml: 1
                                            }}>
                                                تومانءءء
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {!inStock && (
                                    <Box sx={{
                                        py: 0.5,
                                        px: 2,
                                        bgcolor: colors.grey[600],
                                        display: 'inline-block',
                                        borderRadius: 4
                                    }}>
                                        <Typography component="span" sx={{
                                            fontSize: 14,
                                            fontWeight: 400,
                                            color: '#fff',
                                        }}>
                                            ناموجود
                                        </Typography>
                                    </Box>
                                )}

                                {inStock && showProduct.need_inquiry && (
                                    <Box sx={{
                                        py: 0.5,
                                        // px: 2,
                                        display: 'inline-block',
                                        borderRadius: 4
                                    }}>
                                        <Typography component="span" sx={{
                                            fontSize: 14,
                                            fontWeight: 400,
                                            color: colors.grey[800],
                                        }}>
                                            برای اطلاع از قیمت، استعلام بگیرید
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                        <FlexBox sx={{ gap: 1 }}>
                            <PdpAddToCartHandler showProduct={showProduct} brandName={brand?.en_name} siteProductId={extra.siteProductId} site_product_details={{
                                slug: extra.siteProductSlug,
                                title: extra.siteProductTitle
                            }} />
                            {isTriable && !showProduct.need_inquiry && (
                                <PdpAddToTryCartHandler showProduct={showProduct} site_product_details={{
                                    slug: extra.siteProductSlug,
                                    title: extra.siteProductTitle
                                }} />
                            )}
                        </FlexBox>
                    </Paper>
                </Slide>
                </>
            )}
            </NoSsr>
        </>
    )
}